import { Ref, ref } from "@vue/composition-api";
import { isIOS, isMobile } from "@/lib/browserUtils";

export const TRAILER_THUMBNAILS_COUNT = 10;
export const TRAILER_THUMB_WIDTH = 96;

export interface TrailerThumbnail {
  src: string;
}

interface UseTrailerBlobThumbnailsOptions {
  videoEl: Ref<HTMLVideoElement | null>;
  selectedStartSec: Ref<number>;
}

export const useTrailerBlobThumbnails = ({ videoEl, selectedStartSec }: UseTrailerBlobThumbnailsOptions) => {
  const blobThumbnails = ref<TrailerThumbnail[]>([]);
  const thumbnailLoading = ref(false);
  let extractionRunId = 0;
  let extractionVideo: HTMLVideoElement | null = null;

  const revokeObjectUrl = (src?: string | null) => {
    if (src?.startsWith("blob")) {
      URL.revokeObjectURL(src);
    }
  };

  const revokeBlobThumbnails = (excludeSrc?: string | null) => {
    const uniqueSources = new Set(blobThumbnails.value.map(({ src }) => src));
    uniqueSources.forEach(src => {
      if (src !== excludeSrc) revokeObjectUrl(src);
    });
    blobThumbnails.value = [];
  };

  const cleanupExtractionVideo = () => {
    if (!extractionVideo) return;
    extractionVideo.pause();
    extractionVideo.parentElement?.removeChild(extractionVideo);
    extractionVideo.removeAttribute("src");
    extractionVideo.load();
    extractionVideo = null;
  };

  const cancelBlobFrameExtraction = (excludeSrc?: string | null) => {
    extractionRunId += 1;
    cleanupExtractionVideo();
    revokeBlobThumbnails(excludeSrc);
    thumbnailLoading.value = false;
  };

  const waitForVideoEvent = (
    video: HTMLVideoElement,
    eventName: "loadedmetadata" | "seeked",
    runId: number,
    timeoutMs = 30000
  ) => {
    return new Promise<void>((resolve, reject) => {
      let timeoutId: number;
      let cleanup: () => void;
      const ensureCurrentRun = () => {
        if (runId === extractionRunId) return true;
        cleanup();
        reject();
        return false;
      };
      const onEvent = () => {
        if (!ensureCurrentRun()) return;
        cleanup();
        resolve();
      };
      const onError = () => {
        cleanup();
        reject();
      };
      cleanup = () => {
        window.clearTimeout(timeoutId);
        video.removeEventListener(eventName, onEvent);
        video.removeEventListener("error", onError);
      };
      timeoutId = window.setTimeout(() => {
        cleanup();
        reject();
      }, timeoutMs);
      video.addEventListener(eventName, onEvent, { once: true });
      video.addEventListener("error", onError, { once: true });
    });
  };

  const getCanvasObjectUrl = (canvas: HTMLCanvasElement) => {
    return new Promise<string | null>(resolve => {
      if (isMobile()) {
        resolve(canvas.toDataURL("image/jpeg", 0.7));
        return;
      }
      if (canvas.toBlob) {
        canvas.toBlob(blob => resolve(blob ? URL.createObjectURL(blob) : null), "image/jpeg", 0.7);
        return;
      }
      resolve(canvas.toDataURL("image/jpeg", 0.7));
    });
  };

  const waitForFramePaint = () =>
    new Promise<void>(resolve => {
      window.setTimeout(resolve, isIOS() ? 800 : 250);
    });

  const attachExtractionVideo = (video: HTMLVideoElement) => {
    // iOS can return blank frames when extracting from a detached video element.
    if (!isIOS()) return;
    video.style.position = "fixed";
    video.style.left = "-9999px";
    video.style.top = "0";
    video.style.width = "1px";
    video.style.height = "1px";
    video.style.opacity = "0";
    video.style.pointerEvents = "none";
    document.body.appendChild(video);
  };

  const getFrameObjectUrl = async (video: HTMLVideoElement, width: number, height: number) => {
    const canvas = document.createElement("canvas");
    canvas.width = TRAILER_THUMB_WIDTH;
    canvas.height =
      width > 0 ? Math.round(TRAILER_THUMB_WIDTH * (height / width)) : Math.round(TRAILER_THUMB_WIDTH * (9 / 16));
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    return getCanvasObjectUrl(canvas);
  };

  const setRepeatedBlobThumbnail = (src: string) => {
    blobThumbnails.value = Array.from({ length: TRAILER_THUMBNAILS_COUNT }, () => ({ src }));
  };

  const trySetVisibleVideoThumbnail = async (isBlobVideo: boolean) => {
    const video = videoEl.value;
    if (!video || !isBlobVideo || blobThumbnails.value.length || (video.readyState ?? 0) < 2) {
      return;
    }
    try {
      const src = await getFrameObjectUrl(video, video.videoWidth, video.videoHeight);
      if (src && !blobThumbnails.value.length) {
        setRepeatedBlobThumbnail(src);
      }
    } catch {
      // Thumbnail extraction is best-effort; the video itself remains usable.
    }
  };

  const setVisibleVideoThumbnailFallback = async (runId: number) => {
    const video = videoEl.value;
    if (!video || runId !== extractionRunId || (video.readyState ?? 0) < 2) return;
    try {
      const src = await getFrameObjectUrl(video, video.videoWidth, video.videoHeight);
      if (src && runId === extractionRunId) {
        setRepeatedBlobThumbnail(src);
      }
    } catch {
      // Thumbnail extraction is best-effort; the video itself remains usable.
    }
  };

  const extractBlobFrames = async (
    blobUrl: string,
    duration: number,
    videoWidth: number,
    videoHeight: number,
    frameCount = TRAILER_THUMBNAILS_COUNT
  ): Promise<void> => {
    const runId = ++extractionRunId;
    cleanupExtractionVideo();
    revokeBlobThumbnails();
    thumbnailLoading.value = true;

    const canvas = document.createElement("canvas");
    canvas.width = TRAILER_THUMB_WIDTH;
    canvas.height =
      videoWidth > 0
        ? Math.round(TRAILER_THUMB_WIDTH * (videoHeight / videoWidth))
        : Math.round(TRAILER_THUMB_WIDTH * (9 / 16));
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      thumbnailLoading.value = false;
      return;
    }

    const video = document.createElement("video");
    extractionVideo = video;
    video.muted = true;
    video.preload = "metadata";
    video.playsInline = true;
    video.setAttribute("playsinline", "");
    video.setAttribute("webkit-playsinline", "");
    attachExtractionVideo(video);

    const frames: TrailerThumbnail[] = [];

    try {
      video.src = blobUrl;
      video.load();
      await waitForVideoEvent(video, "loadedmetadata", runId);

      for (let i = 0; i < frameCount; i++) {
        if (runId !== extractionRunId) return;
        const framePosition =
          frameCount === 1
            ? selectedStartSec.value + Math.min(30, duration * 0.1)
            : ((i + 0.5) / frameCount) * duration;
        video.currentTime = Math.min(Math.max(0, framePosition), Math.max(0, duration - 0.1));
        await waitForVideoEvent(video, "seeked", runId);
        await waitForFramePaint();
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        const src = await getCanvasObjectUrl(canvas);
        if (src) frames.push({ src });
      }

      if (runId !== extractionRunId) return;
      blobThumbnails.value =
        frameCount === 1 && frames[0] ? Array.from({ length: TRAILER_THUMBNAILS_COUNT }, () => frames[0]) : frames;
    } catch {
      frames.forEach(({ src }) => revokeObjectUrl(src));
      if (runId === extractionRunId) blobThumbnails.value = [];
    } finally {
      if (runId === extractionRunId && !blobThumbnails.value.length) {
        await setVisibleVideoThumbnailFallback(runId);
      }
      if (runId === extractionRunId) thumbnailLoading.value = false;
      if (extractionVideo === video) {
        cleanupExtractionVideo();
      } else {
        video.pause();
        video.parentElement?.removeChild(video);
        video.removeAttribute("src");
        video.load();
      }
    }
  };

  return {
    blobThumbnails,
    thumbnailLoading,
    cancelBlobFrameExtraction,
    extractBlobFrames,
    trySetVisibleVideoThumbnail,
  };
};
